import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useBrands } from "@/contexts/BrandContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { InfoTip } from "@/components/app/InfoTip";
import { UploadPicker } from "@/components/app/UploadPicker";
import { Search, TrendingUp, AlertCircle, ExternalLink } from "lucide-react";

// Map each supported marketplace country → the right Amazon TLD. Keep this in
// sync with backend ``Brand.country`` enum if we ever lock it down.
const AMAZON_DOMAIN_BY_COUNTRY = {
  India: "amazon.in",
  "United States": "amazon.com",
  USA: "amazon.com",
  US: "amazon.com",
  "United Kingdom": "amazon.co.uk",
  UK: "amazon.co.uk",
  Germany: "amazon.de",
  France: "amazon.fr",
  Italy: "amazon.it",
  Spain: "amazon.es",
  Canada: "amazon.ca",
  Mexico: "amazon.com.mx",
  Japan: "amazon.co.jp",
  Australia: "amazon.com.au",
  UAE: "amazon.ae",
  Singapore: "amazon.sg",
  Brazil: "amazon.com.br",
  Netherlands: "amazon.nl",
};

// Real Amazon ASINs start with "B" followed by 9 alphanumerics (10 chars
// total). Anything else (legacy ISBN-style codes, test-data fakes, etc.) is
// rendered as plain text so we don't ship users to an Amazon 404 page.
const ASIN_RE = /^B[A-Z0-9]{9}$/;

/**
 * CompetitorIntel — top ASINs / brands appearing across this week's search demand.
 *
 * Each card now surfaces a precise frequency label ("N keywords") instead of
 * the cryptic ``×N`` and a share-of-search percentage so users can tell at a
 * glance whether a competitor is a real threat or a rounding-error.
 */
export default function CompetitorIntel() {
  const { selectedBrandId, selectedBrand } = useBrands();
  const [data, setData] = useState({ asins: [], brands: [], total_keywords: 0 });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [minShare, setMinShare] = useState("");
  const [uploadId, setUploadId] = useState("");

  // Derive the Amazon TLD from the brand's country. Default to amazon.com when
  // the country isn't in our table — never silently send to amazon.in.
  const amazonDomain = useMemo(
    () => AMAZON_DOMAIN_BY_COUNTRY[selectedBrand?.country] || "amazon.com",
    [selectedBrand],
  );

  // Reset week-pick when brand changes so we don't carry a stale upload_id
  // across brands.
  useEffect(() => { setUploadId(""); }, [selectedBrandId]);

  useEffect(() => {
    if (!selectedBrandId) return;
    setLoading(true);
    const params = new URLSearchParams({ brand_id: selectedBrandId });
    if (uploadId) params.append("upload_id", uploadId);
    api.get(`/dashboard/competitors?${params}`)
      .then((r) => setData(r.data || { asins: [], brands: [], total_keywords: 0 }))
      .catch(() => setData({ asins: [], brands: [], total_keywords: 0 }))
      .finally(() => setLoading(false));
  }, [selectedBrandId, uploadId]);

  const filterFn = (key) => (item) => {
    const q = search.trim().toLowerCase();
    if (q && !item[key].toLowerCase().includes(q)) return false;
    if (minShare && Number(item.share_pct) < Number(minShare)) return false;
    return true;
  };
  const visibleAsins = useMemo(() => data.asins.filter(filterFn("asin")), [data.asins, search, minShare]);
  const visibleBrands = useMemo(() => data.brands.filter(filterFn("brand")), [data.brands, search, minShare]);

  return (
    <div className="space-y-6" data-testid="competitor-intel-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400">Intelligence</p>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Competitor Intelligence</h1>
          <p className="mt-1 text-sm text-slate-500">
            Top clicked ASINs and brands appearing across this week's search demand
            for your category. Each card shows on how many keywords this competitor
            captured the top-click, plus its share of the {data.total_keywords?.toLocaleString() || 0} in-scope keywords this week.
          </p>
        </div>
        <UploadPicker
          brandId={selectedBrandId}
          value={uploadId}
          onChange={setUploadId}
          testId="competitor-upload-picker"
        />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3" data-testid="competitor-filters">
        <div className="relative flex-1 max-w-md">
          <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="competitor-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search ASIN or brand…"
            className="pl-9 h-9 text-sm"
          />
        </div>
        <div className="flex items-center gap-2 text-xs">
          <label className="text-slate-600 inline-flex items-center gap-1">
            Min share %
            <InfoTip term="SHARE_OF_SEARCH" />
          </label>
          <Input
            type="number"
            min="0"
            step="0.5"
            data-testid="competitor-min-share"
            value={minShare}
            onChange={(e) => setMinShare(e.target.value)}
            placeholder="0"
            className="h-9 text-sm w-24"
          />
        </div>
        {data.total_keywords > 0 && (
          <div className="text-xs text-slate-500 ml-auto inline-flex items-center gap-1">
            <TrendingUp className="h-3.5 w-3.5" />
            {data.total_keywords.toLocaleString()} in-scope keywords analysed this week
          </div>
        )}
      </div>

      <Tabs defaultValue="asins">
        <TabsList>
          <TabsTrigger value="asins" data-testid="comp-tab-asins">Top ASINs ({visibleAsins.length})</TabsTrigger>
          <TabsTrigger value="brands" data-testid="comp-tab-brands">Top Brands ({visibleBrands.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="asins">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="comp-asins-grid">
            {visibleAsins.map((a) => {
              const isValidAsin = ASIN_RE.test(a.asin || "");
              return (
                <CompetitorCard
                  key={a.asin}
                  title={a.asin}
                  titleClass="font-mono text-sm font-bold text-slate-900"
                  count={a.count}
                  sharePct={a.share_pct}
                  clusters={a.clusters}
                  sampleTerms={a.sample_terms}
                  asinHref={isValidAsin ? `https://www.${amazonDomain}/dp/${a.asin}` : null}
                  invalidAsin={!isValidAsin}
                />
              );
            })}
            {!visibleAsins.length && (
              <EmptyState
                msg={data.asins.length
                  ? "No ASINs match your filters."
                  : "No competitor ASINs detected — ensure ASIN columns are mapped during upload."}
              />
            )}
          </div>
        </TabsContent>

        <TabsContent value="brands">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="comp-brands-grid">
            {visibleBrands.map((b) => (
              <CompetitorCard
                key={b.brand}
                title={b.brand}
                titleClass="font-semibold text-slate-900"
                count={b.count}
                sharePct={b.share_pct}
                clusters={b.clusters}
              />
            ))}
            {!visibleBrands.length && (
              <EmptyState
                msg={data.brands.length
                  ? "No brands match your filters."
                  : "No competitor brands detected — ensure brand columns are mapped during upload."}
              />
            )}
          </div>
        </TabsContent>
      </Tabs>

      {loading && <div className="text-sm text-slate-500">Loading…</div>}
    </div>
  );
}

function CompetitorCard({ title, titleClass, count, sharePct, clusters, sampleTerms, asinHref, invalidAsin }) {
  // Share band drives the dot color so dominant competitors are immediately visible.
  const tone = sharePct >= 10 ? "text-rose-700 bg-rose-50 border-rose-200"
    : sharePct >= 3 ? "text-amber-700 bg-amber-50 border-amber-200"
    : "text-slate-700 bg-slate-50 border-slate-200";
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow" data-testid={`competitor-card-${title}`}>
      <div className="flex items-start justify-between gap-3">
        <div className={titleClass}>
          {asinHref ? (
            <a
              href={asinHref}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline inline-flex items-center gap-1 group"
              data-testid={`competitor-asin-link-${title}`}
            >
              {title}
              <ExternalLink className="h-3 w-3 opacity-40 group-hover:opacity-100 transition-opacity" />
            </a>
          ) : (
            <span className="inline-flex items-center gap-1" title={invalidAsin ? "Not a valid Amazon ASIN — link disabled" : ""}>
              {title}
              {invalidAsin && <span className="text-[10px] font-sans font-normal text-slate-400">(no link)</span>}
            </span>
          )}
        </div>
        <div className={`shrink-0 inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${tone}`} title={`${sharePct}% share of search`}>
          {sharePct?.toFixed?.(1) ?? sharePct}%
        </div>
      </div>
      <div className="mt-2 text-xs text-slate-500 inline-flex items-center gap-1">
        <span data-testid="competitor-count">{count.toLocaleString()} keyword{count === 1 ? "" : "s"}</span>
        <InfoTip term="COMPETITOR_FREQ" />
      </div>
      {clusters?.length ? (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {clusters.map((c) => (
            <span key={c} className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-600">{c}</span>
          ))}
        </div>
      ) : null}
      {sampleTerms?.length ? (
        <ul className="mt-3 space-y-0.5 border-t border-slate-100 pt-2">
          {sampleTerms.slice(0, 4).map((t, i) => <li key={i} className="text-xs text-slate-600 truncate">· {t}</li>)}
        </ul>
      ) : null}
    </div>
  );
}

function EmptyState({ msg }) {
  return (
    <div className="col-span-full text-sm text-slate-500 py-10 text-center inline-flex items-center gap-1.5 justify-center w-full">
      <AlertCircle className="h-4 w-4 text-slate-400" /> {msg}
    </div>
  );
}
